<template>
	<div v-if="active" class="charge-map-info">
		<div class="info-badge">⚡ Stazioni: {{ stationCount }}</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from "vue";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

interface ChargingStation {
	ID: number;
	AddressInfo: {
		Title: string;
		AddressLine1?: string;
		Town?: string;
		StateOrProvince?: string;
		Postcode?: string;
		CountryID: number;
		Latitude: number;
		Longitude: number;
	};
	Connections?: Array<{
		ConnectionType?: { Title: string };
		PowerKW?: number;
		Quantity?: number;
	}>;
	StatusType?: {
		Title: string;
		IsOperational: boolean;
	};
	UsageType?: {
		Title: string;
	};
	NumberOfPoints?: number;
}

const props = defineProps<{
	map: L.Map | null;
	active: boolean;
}>();

const emit = defineEmits<{
	(e: "stations-loaded", stations: ChargingStation[]): void;
}>();

// Expose getAllStations method for parent components
const getAllStations = () => {
	const allStations: ChargingStation[] = [];
	countryCache.forEach((stations) => allStations.push(...stations));
	return allStations;
};

defineExpose({ getAllStations });

const stationCount = ref(0);
// Use marker clustering for performance with 10k+ markers
const markersLayer = L.markerClusterGroup({
	maxClusterRadius: 80,
	spiderfyOnMaxZoom: true,
	showCoverageOnHover: false,
	zoomToBoundsOnClick: true,
});
const countryCache = new Map<string, ChargingStation[]>();

// Country mapping based on viewport center
const COUNTRY_CODES: { [key: string]: string } = {
	IT: "IT", // Italy
	FR: "FR", // France
	DE: "DE", // Germany
	ES: "ES", // Spain
	GB: "GB", // UK
	NL: "NL", // Netherlands
	BE: "BE", // Belgium
	AT: "AT", // Austria
	CH: "CH", // Switzerland
	PT: "PT", // Portugal
};

watch(
	() => props.active,
	(newVal) => {
		if (newVal && props.map) {
			markersLayer.addTo(props.map);
			fetchStations();
			props.map.on("moveend", onMapMove);
		} else {
			cleanup();
		}
	},
);

function cleanup() {
	if (props.map) {
		props.map.off("moveend", onMapMove);
	}
	markersLayer.clearLayers();
	markersLayer.remove();
	stationCount.value = 0;
}

let moveTimeout: any = null;
function onMapMove() {
	// Debounce map movements
	clearTimeout(moveTimeout);
	moveTimeout = setTimeout(() => {
		fetchStations();
	}, 1000);
}

async function fetchStations() {
	if (!props.map) return;

	const center = props.map.getCenter();
	const countryCode = detectCountry(center.lat, center.lng);

	if (!countryCode) {
		console.warn("[ChargeMap] Could not detect country");
		return;
	}

	// Check cache
	if (countryCache.has(countryCode)) {
		renderStations(countryCache.get(countryCode)!);
		return;
	}

	try {
		// Load from local merged JSON file (downloaded from GitHub ocm-export)
		const response = await fetch(`/data/charging-stations/${countryCode}.json`);

		if (!response.ok) {
			console.error("[ChargeMap] Local file not found:", response.status);
			return;
		}

		const data: ChargingStation[] = await response.json();
		countryCache.set(countryCode, data);
		renderStations(data);
	} catch (error) {
		console.error("[ChargeMap] Load error:", error);
	}
}

function renderStations(stations: ChargingStation[]) {
	if (!props.map) return;

	markersLayer.clearLayers();
	const bounds = props.map.getBounds();
	let visibleCount = 0;

	stations.forEach((station) => {
		const { Latitude, Longitude } = station.AddressInfo;
		if (!Latitude || !Longitude) return;

		// Only show stations in current viewport
		if (!bounds.contains([Latitude, Longitude])) return;

		visibleCount++;

		const icon = L.divIcon({
			className: "charge-station-marker",
			html: `
        <div class="marker-container">
          <div class="marker-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.5 11L13.5 14H16L11.5 20V14H9L10 11H7L11.5 4V10H14.5V11Z" fill="white"/>
            </svg>
          </div>
        </div>
      `,
			iconSize: [40, 40],
			iconAnchor: [20, 20],
		});

		const marker = L.marker([Latitude, Longitude], { icon });

		// Build popup content
		let popupContent = `<div style="min-width: 200px;">
      <h4 style="margin: 0 0 8px 0; color: #1976d2;">⚡ ${
				station.AddressInfo.Title || "Stazione di Ricarica"
			}</h4>`;

		if (station.AddressInfo.AddressLine1 || station.AddressInfo.Town) {
			popupContent += `<p style="margin: 4px 0; font-size: 0.9em;">
        📍 ${station.AddressInfo.AddressLine1 || ""} ${
				station.AddressInfo.Town || ""
			}
      </p>`;
		}

		if (station.StatusType) {
			const statusColor = station.StatusType.IsOperational
				? "#4caf50"
				: "#f44336";
			popupContent += `<p style="margin: 4px 0; color: ${statusColor}; font-weight: 600;">
        ${station.StatusType.Title}
      </p>`;
		}

		if (station.Connections && station.Connections.length > 0) {
			popupContent += `<div style="margin-top: 8px; font-size: 0.85em;">
        <strong>Connettori:</strong><ul style="margin: 4px 0; padding-left: 20px;">`;

			station.Connections.slice(0, 3).forEach((conn) => {
				const type = conn.ConnectionType?.Title || "Sconosciuto";
				const power = conn.PowerKW ? `${conn.PowerKW} kW` : "";
				const qty = conn.Quantity ? `x${conn.Quantity}` : "";
				popupContent += `<li>${type} ${power} ${qty}</li>`;
			});

			if (station.Connections.length > 3) {
				popupContent += `<li>... e altri ${
					station.Connections.length - 3
				}</li>`;
			}

			popupContent += `</ul></div>`;
		}

		if (station.NumberOfPoints) {
			popupContent += `<p style="margin: 4px 0; font-size: 0.85em;">🔌 Punti: ${station.NumberOfPoints}</p>`;
		}

		popupContent += `</div>`;

		marker.bindPopup(popupContent);
		marker.addTo(markersLayer);
	});

	stationCount.value = visibleCount;
}

function detectCountry(lat: number, lng: number): string | null {
	// Simple heuristic based on lat/lng ranges
	// Italy
	if (lat >= 36 && lat <= 47 && lng >= 6 && lng <= 19) return "IT";
	// France
	if (lat >= 41 && lat <= 51 && lng >= -5 && lng <= 10) return "FR";
	// Germany
	if (lat >= 47 && lat <= 55 && lng >= 5 && lng <= 15) return "DE";
	// Spain
	if (lat >= 36 && lat <= 44 && lng >= -10 && lng <= 5) return "ES";
	// UK
	if (lat >= 49 && lat <= 61 && lng >= -8 && lng <= 2) return "GB";
	// Netherlands
	if (lat >= 50 && lat <= 54 && lng >= 3 && lng <= 8) return "NL";
	// Belgium
	if (lat >= 49 && lat <= 52 && lng >= 2 && lng <= 7) return "BE";
	// Austria
	if (lat >= 46 && lat <= 49 && lng >= 9 && lng <= 18) return "AT";
	// Switzerland
	if (lat >= 45 && lat <= 48 && lng >= 5 && lng <= 11) return "CH";
	// Portugal
	if (lat >= 37 && lat <= 42 && lng >= -10 && lng <= -6) return "PT";

	// Default to Italy if no match
	return "IT";
}

onBeforeUnmount(() => {
	cleanup();
});
</script>

<style scoped>
.charge-map-info {
	position: absolute;
	bottom: 45px;
	right: 20px;
	z-index: 2000;
}

@media (max-width: 768px) {
	.charge-map-info {
		bottom: 60px;
	}
}

.info-badge {
	background: rgba(25, 118, 210, 0.95);
	backdrop-filter: blur(10px);
	color: white;
	padding: 8px 12px;
	border-radius: 6px;
	font-size: 0.85rem;
	font-weight: 600;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

:global(.charge-station-marker) {
	background: transparent !important;
	border: none !important;
}

:global(.charge-station-marker .marker-container) {
	position: relative;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
}

:global(.charge-station-marker .marker-circle) {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(76, 175, 80, 0.3);
	border: 2px solid rgba(255, 255, 255, 0.9);
	transition: all 0.2s ease;
	cursor: pointer;
}

:global(.charge-station-marker .marker-circle:hover) {
	transform: scale(1.1);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15), 0 6px 12px rgba(76, 175, 80, 0.4);
}

:global(.charge-station-marker svg) {
	filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}
</style>
